import React, { useState } from 'react';
import styled from 'styled-components';
import { Stack } from '@construct-kit/core';
import { layout } from '../../../../../styles/_variables';
import { useFetch } from '../../../useFetch';
import reportError from '../../../../client/tracking/errorReporter';
import ElitePlusCarouselSkeleton from './ElitePlusCarouselSkeleton';
import ElitePlusCarouselComponent from './ElitePlusCarouselComponent';
import type {
  ElitePlusCarouselListing,
  ElitePlusCarouselListingBucket,
} from '../../model/elitePlusCarouselListingResponse';
import { getElitePlusCarouselListings } from '../../model/homepageBffRepository';
import decodeToken from '../../../../shared/tracking/decodeToken';
import { getCookie } from '../../../../client/cookie/cookieService';

const Container = styled(Stack)`
  margin-top: 2.25rem;
  overflow: hidden;
  ${({ theme }) => theme.layoutBreakpoints.large} {
    width: 100%;
    max-width: ${layout.maxWidthDesktopWide};
    margin: 3rem auto 0 auto;
  }
`;

export type ElitePlusCarouselContentRendererProps = {
  listings: ElitePlusCarouselListing[];
  bucket?: ElitePlusCarouselListingBucket;
  error?: Error;
};

const ElitePlusCarouselContentRenderer: React.FunctionComponent<
  ElitePlusCarouselContentRendererProps
> = ({ listings, bucket, error }) => {
  if (listings.length > 0 && bucket && !error) {
    return <ElitePlusCarouselComponent listings={listings} bucket={bucket} />;
  }
  if (error) {
    return null;
  }
  return <ElitePlusCarouselSkeleton />;
};

const ElitePlusCarousel: React.FunctionComponent = () => {
  const [listings, setListings] = useState<ElitePlusCarouselListing[]>([]);
  const [bucket, setBucket] = useState<ElitePlusCarouselListingBucket | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  const getUserId = (): string | undefined => {
    const token = getCookie('lmdstok');
    const isSignedIn = !!token;

    return isSignedIn ? decodeToken(token).uid : getCookie('rcauid');
  };

  useFetch(
    () =>
      (listings.length > 0
        ? Promise.resolve(listings)
        : getElitePlusCarouselListings(getUserId()).then((response) => {
          setListings(response.listings);
          setBucket(response.bucket);
        })),
    (e: Error) => {
      reportError(e, 'Could not load elite plus carousel listings');
      setError(e);
    },
    20000,
  );

  return (
    <Container inset="0 medium 0 medium" gap="medium">
      <ElitePlusCarouselContentRenderer listings={listings} bucket={bucket} error={error} />
    </Container>
  );
};

export default ElitePlusCarousel;
